import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useLoaderData, useActionData, useBlocker } from "react-router-dom";
import { FullPagePaperLayout } from "../Layouts/FullPagePaperLayout";
import { PageBreadcrumbs, PageTitle, PageSubtitle, PageActions } from "../Layouts/components/PageTitleContainer";
import { UploadLocationsDialog } from "./UploadLocationsDialog";
import {
  Autocomplete,
  Stack,
  Button,
  Box,
  Select,
  MenuItem,
  Alert,
  SelectChangeEvent,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import useSWR from "swr";
import { api, wrappedCollectionFetcher } from "services/api.service";
import { useGlobalAlert } from "hooks/useAlert";
import useCurrentUser from "hooks/useCurrentUser";
import { withActionErrorAlert } from "utils/withActionError";
import { LocationManagementLocation } from "types/locationManagement/location";
import { LocationManagementLocationKind } from "types/locationManagement/locationKind";
import { LocationManagementLocationType } from "types/locationManagement/locationType";
import { FieldControl } from "components/Form/FieldControl";
import { Form } from "components/Form/Form";
import { ParentLocationPicker } from "./ParentLocationPicker";
import { LocationContents } from "./LocationContents";
import { LocationLabelPrint } from "./LocationLabelPrint";

// An empty select value leaves the outlined label sitting on top of the selected item, so "no type" uses its own value.
const NO_LOCATION_TYPE = "none";
const NO_LOCATION_KIND = "none";
const LOCATION_TYPES_KEY = "/location_management/location_types?page[size]=100";
const LOCATION_KINDS_KEY = "/location_management/location_kinds?page[size]=100";

const fetchLocationTypes = (url: string) =>
  wrappedCollectionFetcher<LocationManagementLocationType>(url).then((res) => res.rows || []);

const fetchLocationKinds = (url: string) =>
  wrappedCollectionFetcher<LocationManagementLocationKind>(url).then((res) => res.rows || []);

// The select icon slot is centered as a 1em box. Match that box so the spinner sits where the arrow does.
const LoadingDropdownIcon = ({ className }: { className?: string }) => (
  <Box
    component="span"
    className={className}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      // Match the dropdown arrow, which is a 1.5rem icon centered with top: calc(50% - 0.5em).
      fontSize: "1.5rem",
      width: "1em",
      height: "1em",
    }}
  >
    <CircularProgress color="inherit" size="1em" />
  </Box>
);

const locationTypePrefix = (type: LocationManagementLocationType) =>
  type.name.toLowerCase() === "quarantine" ? "QRTN" : type.name.toUpperCase();

// The longest matching prefix wins when one starts with another.
const locationTypeIdForName = (name: string, locationTypes: LocationManagementLocationType[]) => {
  const upperName = name.toUpperCase();
  const match = locationTypes
    .filter((locationType) => upperName.startsWith(`${locationTypePrefix(locationType)}-`))
    .sort((left, right) => locationTypePrefix(right).length - locationTypePrefix(left).length)[0];
  return match ? String(match.id) : null;
};

interface LocationFormFieldsProps {
  location: LocationManagementLocation;
  isEdit: boolean;
}

const savedTypeId = (location: LocationManagementLocation) =>
  location.locationTypeId ? String(location.locationTypeId) : NO_LOCATION_TYPE;

const savedKindId = (location: LocationManagementLocation) =>
  location.locationKindId ? String(location.locationKindId) : NO_LOCATION_KIND;

const savedParentId = (location: LocationManagementLocation) => (location.parent?.id ? String(location.parent.id) : "");

type InsideLocation = { id: string; code: string; warehouse?: boolean };

const savedInside = (location: LocationManagementLocation): InsideLocation | null =>
  location.parent?.id ? { id: String(location.parent.id), code: location.parent.code } : null;

const sameInside = (left: InsideLocation | null, right: InsideLocation | null) =>
  (left?.id ?? "") === (right?.id ?? "") && (left?.code ?? "") === (right?.code ?? "");

// One warehouse location belongs to the building the user is working in.
const assignedWarehouseKey = (warehouseId: number) =>
  `/location_management/locations?page[size]=1&filter[]=${encodeURIComponent(`warehouse_id,is,${warehouseId}`)}`;

const fetchAssignedWarehouse = (url: string) =>
  wrappedCollectionFetcher<LocationManagementLocation>(url).then((res) => res.rows || []);

interface LocationDraft {
  code: string;
  description: string;
  locationTypeId: string;
  locationKindId: string;
  parentId: string;
  organizationId: string;
}

const sameDraft = (left: LocationDraft, right: LocationDraft) =>
  left.code === right.code &&
  left.description === right.description &&
  left.locationTypeId === right.locationTypeId &&
  left.locationKindId === right.locationKindId &&
  left.parentId === right.parentId &&
  left.organizationId === right.organizationId;

const formText = (data: FormData, field: string) => {
  const value = data.get(field);
  return typeof value === "string" ? value : "";
};

// Most location names are codes. Warehouses and a few word names keep the case they were given.
const CASE_PRESERVED_NAMES = ["invoiced", "ewasted", "missing"];

const keepsTypedCase = (value: string, warehouse: boolean) => {
  if (warehouse) return true;
  const name = value
    .replace(/[\r\n]/g, "")
    .trim()
    .toLowerCase();
  return name.length > 0 && CASE_PRESERVED_NAMES.some((preserved) => preserved.startsWith(name));
};

const locationNameFromInput = (value: string, preserveCase: boolean) => {
  const stripped = value.replace(/[\r\n]/g, "");
  return preserveCase ? stripped : stripped.toUpperCase();
};

const LocationFormFields: React.FC<LocationFormFieldsProps> = ({ location, isEdit }) => {
  const { user } = useCurrentUser();
  const { data: fetchedTypes, isLoading: typesLoading } = useSWR(LOCATION_TYPES_KEY, fetchLocationTypes, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
  });
  const { data: fetchedKinds, isLoading: kindsLoading } = useSWR(LOCATION_KINDS_KEY, fetchLocationKinds, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
  });
  const locationTypes =
    fetchedTypes && fetchedTypes.length > 0 ? fetchedTypes : location.locationType ? [location.locationType] : [];
  const locationKinds =
    fetchedKinds && fetchedKinds.length > 0 ? fetchedKinds : location.locationKind ? [location.locationKind] : [];
  const [code, setCode] = useState<string>(location.code || "");
  const [description, setDescription] = useState<string>(location.description || "");
  const [locationTypeId, setLocationTypeId] = useState<string>(
    location.locationTypeId ? String(location.locationTypeId) : NO_LOCATION_TYPE,
  );
  const [locationKindId, setLocationKindId] = useState<string>(savedKindId(location));
  const [organizationId, setOrganizationId] = useState(String(location.organizationId || ""));
  const [selectedClient, setSelectedClient] = useState<{ id: string | number; name: string } | null>(() =>
    location.organizationId
      ? { id: location.organizationId, name: location.organizationName || "Selected client" }
      : null,
  );
  const [clientQuery, setClientQuery] = useState("");
  const {
    data: clients,
    isLoading: clientsLoading,
    error: clientsError,
  } = useSWR(
    `/organizations?q=${encodeURIComponent(clientQuery)}`,
    (url: string) => api.get(url).then(({ data }) => data.organizations as { id: string | number; name: string }[]),
    { keepPreviousData: true, revalidateOnFocus: false },
  );
  const clientOptions = clients || [];
  const [parentId, setParentId] = useState<string>(savedParentId(location));
  const [insideDefault, setInsideDefault] = useState<InsideLocation | null>(() => savedInside(location));
  const parentEdited = useRef(false);
  // Inside Location is optional. Only a new location starts in the signed-in user's warehouse.
  const assignedWarehouseId = !isEdit && user.warehouseId ? user.warehouseId : null;
  const { data: assignedWarehouseRows } = useSWR(
    assignedWarehouseId ? assignedWarehouseKey(assignedWarehouseId) : null,
    fetchAssignedWarehouse,
    { revalidateOnFocus: false, dedupingInterval: 60_000 },
  );
  const assignedRow = (assignedWarehouseRows || []).find(
    (row) => Number(row.warehouseId) === Number(assignedWarehouseId) && String(row.id) !== String(location.id),
  );
  const assignedParentId = assignedRow ? String(assignedRow.id) : "";
  const assignedParentCode = assignedRow?.code ?? "";
  const assignedParentRef = useRef<InsideLocation | null>(null);
  assignedParentRef.current = assignedParentId
    ? { id: assignedParentId, code: assignedParentCode, warehouse: true }
    : null;
  const [formResetKey, setFormResetKey] = useState(0);
  const [savingBeforeLeave, setSavingBeforeLeave] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const insideInputRef = useRef<HTMLInputElement>(null);
  const descriptionInputRef = useRef<HTMLInputElement>(null);
  const allowLeave = useRef(false);
  const pendingRef = useRef(false);
  const submittedFields = useRef<LocationDraft | null>(null);
  const savedFields = useRef<LocationDraft | null>(null);
  // Set only when the name field changes. The route keeps this form mounted, so `code` can still be the
  // previous location's name for the render that loads the next one.
  const typedName = useRef<string | null>(null);
  const loadedCode = location.code || "";
  const loadedDescription = location.description || "";
  const loadedTypeId = savedTypeId(location);
  const loadedKindId = savedKindId(location);
  const loadedParentId = savedParentId(location);
  const loadedOrganizationId = String(location.organizationId || "");
  const actionData = useActionData() as { errors?: unknown; created?: boolean; updated?: boolean } | undefined;
  // Update stays on this page and the loader refetch lands later. The submitted draft is already saved,
  // so leaving must not wait on that refetch. A later edit no longer matches it and blocks again.
  const seenActionData = useRef(actionData);
  if (seenActionData.current !== actionData) {
    seenActionData.current = actionData;
    if (actionData?.updated && submittedFields.current) savedFields.current = submittedFields.current;
  }
  const currentDraft = { code, description, locationTypeId, locationKindId, parentId, organizationId };
  const pending =
    isEdit &&
    !sameDraft(currentDraft, {
      code: loadedCode,
      description: loadedDescription,
      locationTypeId: loadedTypeId,
      locationKindId: loadedKindId,
      parentId: loadedParentId,
      organizationId: loadedOrganizationId,
    }) &&
    (savedFields.current === null || !sameDraft(currentDraft, savedFields.current));
  pendingRef.current = pending;
  const blocker = useBlocker(
    useCallback(({ currentLocation, nextLocation }) => {
      if (!pendingRef.current || allowLeave.current) return false;
      return currentLocation.pathname !== nextLocation.pathname;
    }, []),
  );
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  // The edit route reuses this component when the location id changes, so the initial state would otherwise stay put.
  // The loader also refetches after an update, and the fields follow the saved values then too.
  useEffect(() => {
    typedName.current = null;
    parentEdited.current = false;
    setCode(loadedCode);
    setDescription(loadedDescription);
    setLocationTypeId(loadedTypeId);
    setLocationKindId(loadedKindId);
    const nextInside = loadedParentId
      ? { id: loadedParentId, code: location.parent?.code || "" }
      : isEdit
        ? null
        : assignedParentRef.current;
    setParentId(nextInside?.id ?? "");
    setInsideDefault((current) => (sameInside(current, nextInside) ? current : nextInside));
    setOrganizationId(loadedOrganizationId);
    setSelectedClient(
      loadedOrganizationId ? { id: loadedOrganizationId, name: location.organizationName || "Selected client" } : null,
    );
  }, [
    loadedCode,
    loadedDescription,
    loadedTypeId,
    loadedKindId,
    loadedParentId,
    loadedOrganizationId,
    location.organizationName,
    location.parent?.code,
    isEdit,
  ]);

  // Only a different location starts over at the name. Saving keeps the cursor where it was.
  useEffect(() => {
    allowLeave.current = false;
    nameInputRef.current?.focus();
  }, [location.id]);

  useEffect(() => {
    if (actionData?.errors) allowLeave.current = false;
    if (!actionData?.created) return;
    typedName.current = null;
    setCode("");
    setDescription("");
    setLocationTypeId(NO_LOCATION_TYPE);
    setLocationKindId(NO_LOCATION_KIND);
    parentEdited.current = false;
    setParentId(assignedParentRef.current?.id ?? "");
    setInsideDefault(assignedParentRef.current);
    setOrganizationId("");
    setSelectedClient(null);
    setClientQuery("");
    setFormResetKey((key) => key + 1);
  }, [actionData]);

  useEffect(() => {
    if (formResetKey === 0) return;
    nameInputRef.current?.focus();
  }, [formResetKey]);

  // A new location starts inside the signed-in user's warehouse.
  // Focus alone is not a choice: a scanned name moves here before this lookup returns.
  // Text already in the field, or a code whose lookup has not finished, is a parent they started.
  useEffect(() => {
    if (isEdit || parentEdited.current || !assignedParentId) return;
    const typedInside = insideInputRef.current?.value.replace(/[\r\n]/g, "").trim() ?? "";
    if (typedInside) return;
    setParentId((current) => (current === assignedParentId ? current : assignedParentId));
    setInsideDefault((current) => {
      const next = { id: assignedParentId, code: assignedParentCode, warehouse: true };
      return sameInside(current, next) ? current : next;
    });
  }, [assignedParentId, assignedParentCode, isEdit]);

  useEffect(() => {
    if (!pending) return undefined;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [pending]);

  const warehouseLocation = Boolean(location.warehouseId);
  const applyLocationName = (nextName: string) => {
    const name = locationNameFromInput(nextName, keepsTypedCase(nextName, warehouseLocation));
    typedName.current = name;
    setCode(name);
    if (!warehouseLocation) {
      const matchedTypeId = locationTypeIdForName(name, fetchedTypes || []);
      if (matchedTypeId) setLocationTypeId(matchedTypeId);
    }
    // A scanner ends the code with a line break. That should move to the next field.
    return /[\r\n]/.test(nextName);
  };

  // Types often arrive after the name is typed. Match again so WHOL- still selects WHOL.
  // A name loaded with the location keeps its saved type. `code` can still be the previous location's
  // name on the render that opens the next one, and that stale prefix must not select a type.
  useEffect(() => {
    if (warehouseLocation) return;
    if (typedName.current !== code || code === loadedCode) return;
    const matchedTypeId = locationTypeIdForName(code, fetchedTypes || []);
    if (matchedTypeId) setLocationTypeId(matchedTypeId);
  }, [code, loadedCode, fetchedTypes, warehouseLocation]);

  const handleLocationTypeChange = (event: SelectChangeEvent<string>) => {
    const nextTypeId = event.target.value;
    setLocationTypeId(nextTypeId);
    const type = locationTypes.find((locationType) => locationType.id.toString() === nextTypeId);
    if (type) {
      setCode(`${locationTypePrefix(type)}-`);
    }
  };

  const stayOnPage = () => {
    if (blockerRef.current.state !== "blocked") return;
    blockerRef.current.reset();
  };

  const skipUnsavedChanges = () => {
    if (blockerRef.current.state !== "blocked") return;
    allowLeave.current = true;
    blockerRef.current.proceed();
  };

  const quarantineType =
    locationTypes.find((type) => String(type.id) === locationTypeId)?.name.toLowerCase() === "quarantine";
  const prefixError =
    quarantineType &&
    !code.toUpperCase().startsWith("QRTN-") &&
    (!isEdit || code !== location.code || locationTypeId !== savedTypeId(location))
      ? "Quarantine locations must start with QRTN-"
      : "";
  useEffect(() => {
    nameInputRef.current?.setCustomValidity(prefixError);
  }, [prefixError]);

  const updateAndLeave = async () => {
    if (blockerRef.current.state !== "blocked" || !location.id) return;
    if (prefixError) {
      blockerRef.current.reset();
      nameInputRef.current?.focus();
      return;
    }
    setSavingBeforeLeave(true);
    let saved = false;
    try {
      const formData = new FormData();
      formData.set("location[code]", code);
      formData.set("location[description]", description);
      formData.set(
        "location[location_type_id]",
        warehouseLocation || locationTypeId === NO_LOCATION_TYPE ? "" : locationTypeId,
      );
      formData.set("location[location_kind_id]", locationKindId === NO_LOCATION_KIND ? "" : locationKindId);
      formData.set("location[parent_id]", warehouseLocation ? "" : parentId);
      formData.set("location[organization_id]", organizationId);
      const res = await withActionErrorAlert(() =>
        api.request({
          method: "PUT",
          url: `/location_management/locations/${location.id}`,
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        }),
      );
      saved = res?.status === 200 && blockerRef.current.state === "blocked";
      if (!saved && blockerRef.current.state === "blocked") blockerRef.current.reset();
    } catch {
      // Network failures and many 500s have no object-shaped errors, so the alert helper throws
      // before it can show one. Leave the blocker in place so Skip, Update, and dismiss still work.
      useGlobalAlert.alertError("There was an error, please try again");
    } finally {
      setSavingBeforeLeave(false);
    }
    if (saved) {
      allowLeave.current = true;
      blockerRef.current.proceed();
    }
  };

  return (
    <Form
      method={isEdit ? "PUT" : "POST"}
      resource="location"
      onSubmit={(event) => {
        const data = new FormData(event.currentTarget);
        const submittedType = formText(data, "location[location_type_id]");
        const submittedKind = formText(data, "location[location_kind_id]");
        submittedFields.current = {
          code: formText(data, "location[code]"),
          description: formText(data, "location[description]"),
          locationTypeId: submittedType || NO_LOCATION_TYPE,
          locationKindId: submittedKind || NO_LOCATION_KIND,
          parentId: formText(data, "location[parent_id]"),
          organizationId: formText(data, "location[organization_id]"),
        };
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter") return;
        const target = event.target as HTMLElement;
        if (target === descriptionInputRef.current) return;
        if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") return;
        event.preventDefault();
      }}
    >
      <Stack direction="column" spacing={2}>
        {warehouseLocation && (
          <Alert severity="info">
            This is the fixed location for the warehouse. Other locations are filed into it, and it can&apos;t be moved.
          </Alert>
        )}
        <FieldControl name="locationTypeId" label="Location Type">
          {({ label, labelId }) => (
            <>
              <input
                type="hidden"
                name="location[location_type_id]"
                value={warehouseLocation || locationTypeId === NO_LOCATION_TYPE ? "" : locationTypeId}
              />
              <Select
                labelId={labelId}
                value={warehouseLocation ? NO_LOCATION_TYPE : locationTypeId}
                label={label}
                disabled={warehouseLocation}
                onChange={handleLocationTypeChange}
                IconComponent={typesLoading ? LoadingDropdownIcon : undefined}
              >
                <MenuItem value={NO_LOCATION_TYPE}>No type (fixed location)</MenuItem>
                {locationTypes.map((locationType) => (
                  <MenuItem key={locationType.id} value={String(locationType.id)}>
                    {locationType.name}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
        </FieldControl>
        <input type="hidden" name="location[organization_id]" value={organizationId} />
        <Autocomplete
          options={clientOptions}
          value={selectedClient}
          loading={clientsLoading}
          getOptionLabel={(client) => client.name}
          isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
          filterOptions={(options) => options}
          onInputChange={(_event, value, reason) => {
            if (reason === "input") setClientQuery(value);
          }}
          onChange={(_event, value) => {
            setOrganizationId(value ? String(value.id) : "");
            setSelectedClient(value);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Client"
              error={Boolean(clientsError)}
              helperText={
                clientsError
                  ? "Unable to load clients. Try again."
                  : "Required for Quarantine. Leave empty for a shared location."
              }
            />
          )}
        />
        <FieldControl name="locationKindId" label="Location Kind">
          {({ label, labelId }) => (
            <>
              <input
                type="hidden"
                name="location[location_kind_id]"
                value={locationKindId === NO_LOCATION_KIND ? "" : locationKindId}
              />
              <Select
                labelId={labelId}
                value={locationKindId}
                label={label}
                onChange={(event) => setLocationKindId(event.target.value)}
                IconComponent={kindsLoading ? LoadingDropdownIcon : undefined}
              >
                <MenuItem value={NO_LOCATION_KIND}>No kind</MenuItem>
                {locationKinds.map((locationKind) => (
                  <MenuItem key={locationKind.id} value={String(locationKind.id)}>
                    {locationKind.name}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
        </FieldControl>
        <FieldControl
          name="code"
          variant="outlined"
          label="Location Name"
          helperText={prefixError || undefined}
          defaultValue={location.code || ""}
          component={TextField}
          inputProps={{
            value: code,
            error: Boolean(prefixError),
            autoFocus: true,
            inputRef: nameInputRef,
            onChange: (event) => {
              const input = event.target;
              const start = input.selectionStart ?? input.value.length;
              const end = input.selectionEnd ?? start;
              const preserveCase = keepsTypedCase(input.value, warehouseLocation);
              const nextStart = locationNameFromInput(input.value.slice(0, start), preserveCase).length;
              const nextEnd = locationNameFromInput(input.value.slice(0, end), preserveCase).length;
              const advanced = applyLocationName(input.value);
              if (advanced) {
                insideInputRef.current?.focus();
                return;
              }
              requestAnimationFrame(() => {
                nameInputRef.current?.setSelectionRange(nextStart, nextEnd);
              });
            },
            onKeyDown: (event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              insideInputRef.current?.focus();
            },
          }}
        />
        <ParentLocationPicker
          key={formResetKey}
          name="location[parent_id]"
          inputRef={insideInputRef}
          disabled={warehouseLocation}
          defaultValue={insideDefault}
          excludeId={location.id}
          onChange={(nextParentId) => {
            parentEdited.current = true;
            setParentId(nextParentId);
          }}
          onCommit={() => {
            parentEdited.current = true;
          }}
        />
        <FieldControl
          name="description"
          variant="outlined"
          label="Description"
          inputProps={{
            value: description,
            inputRef: descriptionInputRef,
            onChange: (event) => setDescription(event.target.value),
          }}
          component={TextField}
        />
        <div>
          <Button type="submit" variant="contained">
            {isEdit ? "Update" : "Create"}
          </Button>
        </div>
        <LocationLabelPrint code={code} />
      </Stack>
      <Dialog open={blocker.state === "blocked"} onClose={savingBeforeLeave ? undefined : stayOnPage}>
        <DialogTitle>Unsaved changes</DialogTitle>
        <DialogContent>
          <DialogContentText>Update this location before leaving, or skip the unsaved changes.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={skipUnsavedChanges} disabled={savingBeforeLeave}>
            Skip
          </Button>
          <Button type="button" onClick={updateAndLeave} variant="contained" disabled={savingBeforeLeave}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Form>
  );
};

const LocationForm: React.FC = () => {
  const { locationId } = useParams<{ locationId?: string }>();
  const isEdit = Boolean(locationId);
  const [uploadOpen, setUploadOpen] = useState(false);
  const { location, errors } = useLoaderData() as {
    location: LocationManagementLocation;
    errors?: { form: string[] };
  };

  return (
    <FullPagePaperLayout>
      <Box sx={{ overflow: "auto", minHeight: 0, flex: "1 1 auto" }}>
        <PageBreadcrumbs links={[{ label: "Warehouse" }, { label: "Locations", path: "/locations" }]} />
        <PageTitle>{isEdit ? "Edit" : "Create"} Location</PageTitle>
        {!isEdit && (
          <PageActions>
            <Button variant="outlined" onClick={() => setUploadOpen(true)}>
              Upload locations
            </Button>
          </PageActions>
        )}
        {uploadOpen && <UploadLocationsDialog open onClose={() => setUploadOpen(false)} />}
        <PageSubtitle>
          Locations represent specific, real-world facility sites like shelves, desks, and floor spots. Every location
          must be unique. Each warehouse has a fixed location that other locations are filed into. That warehouse
          location can&apos;t be moved. A location with a type can be moved. A location without a type can be placed
          inside another location without a type.
        </PageSubtitle>
        <Box sx={{ m: 3 }}>
          {errors?.form && <Alert severity="error">{errors.form}</Alert>}
          {/* The edit route reuses this page, so a different location has to mount a new form.
              The contents below already use the location id as their key. */}
          <LocationFormFields key={`location-form-${location.id ?? "new"}`} location={location} isEdit={isEdit} />
          {isEdit && <LocationContents key={location.id} location={location} />}
        </Box>
      </Box>
    </FullPagePaperLayout>
  );
};

export default LocationForm;
